import React, {
	type ComponentType,
	useCallback,
	useLayoutEffect,
	type ReactNode,
	useMemo,
	useRef,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	Fragment,
	type PropsWithChildren,
} from 'react';
import { css, styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, EntryPointContainer, type PreloadedEntryPoint } from 'react-relay';
import { parse, stringify } from 'query-string';
import { IssueViewSkeleton } from '@jira/issue__issue-view-skeleton/src/IssueViewSkeleton.tsx';
import { token } from '@atlaskit/tokens';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { UnknownErrorView } from '@atlassian/jira-issue-view-errors/src/ui/unknown-error-view/index.tsx';
import {
	ChangeEventTypes,
	type ChangeEvent,
} from '@atlassian/jira-issue-view-model/src/change-type.tsx';
import { IssueBoundary } from '@atlassian/jira-issue-view/src/async.tsx';
import type IssueAppType from '@atlassian/jira-issue-view/src/views/issue-details/issue-app.tsx';
import { getIssueContainerIncreasedMaxWidth } from '@atlassian/jira-issue-view/src/views/issue-details/issue-layout/issueLayoutDimensions.constants.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { SpaStatePageErrorReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { IssueNavigatorData } from '@atlassian/jira-issue-context-service/src/types.tsx';
import { useIssueContextActions } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { AnalyticsSource } from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { EntryPointReferencePlaceholder } from '@atlassian/jira-entry-point-placeholder/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { useStartNinIssueAppAnalytics } from '../../../controllers/issue-app-page-segment-analytics/index.tsx';
import {
	useSelectedIssueDebouncedState,
	useIsFullPageIssueAppMode,
	useSelectedIssueActions,
	useIssueKeys,
	useIssuesCount,
} from '../../../controllers/selected-issue/hooks.tsx';
import { useOnDeleteIssue } from '../../../services/on-delete-issue/index.tsx';
import { useIssueByFieldsRefetch } from '../../../services/refetch-issue/index.tsx';
import { ANALYTICS_SOURCE } from '../../constants.tsx';
import { markOnce, marks } from '../../utils/performance-analytics.tsx';
import type { main_issueNavigator_IssueAppWithData_view$key as ViewKey } from './__generated__/main_issueNavigator_IssueAppWithData_view.graphql';
import type { main_issueNavigator_IssueAppWithData_issueResults$key as IssueAppKey } from './__generated__/main_issueNavigator_IssueAppWithData_issueResults.graphql';
import HeaderActions from './header-actions/index.tsx';
import { useScrollToActivityFeed } from './utils.tsx';

type IssueKey = string;

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-app-container">{children}</UFOSegment>
	),
	Fragment,
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AsyncIssueAppComponent = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-issue-app" */ '@atlassian/jira-issue-view/src/views/issue-details/issue-app'
		),
	{ ssr: false },
);

type IssueAppProps = JSX.LibraryManagedAttributes<
	typeof IssueAppType,
	ComponentProps<typeof IssueAppType>
>;

type OnIssueKeyChangeArgument = {
	fromIssueKey: IssueKey;
	toIssueKey: IssueKey;
	meta?: {
		location: string;
	};
};

type IssueAppWithBoundaryProps = {
	/**
	 * Flag to adjust skeleton spacing based on whether the component is in an embedded or full page layout.
	 */
	isEmbedView: boolean;
	issueAppEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
} & IssueAppProps;

const IssueAppWithConditionalEntrypoint = ({
	isEmbedView,
	issueAppEntryPoint,
	...rest
}: IssueAppWithBoundaryProps) => {
	if (issueAppEntryPoint) {
		return (
			<UFOSegment name="issue-app-entrypoint">
				<JSErrorBoundary
					id="issue-app-entrypoint"
					packageName="issue-navigator"
					fallback={() => <UnknownErrorView />}
				>
					<EntryPointReferencePlaceholder
						name="issue-app"
						fallback={<IssueViewSkeleton isEmbedView={isEmbedView} />}
						entryPointReference={issueAppEntryPoint}
					>
						<EntryPointContainer
							entryPointReference={issueAppEntryPoint}
							// EntryPointContainer can't infer the type of props from entryPointReference correctly
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							props={rest as never}
						/>
					</EntryPointReferencePlaceholder>
				</JSErrorBoundary>
			</UFOSegment>
		);
	}

	return <AsyncIssueAppUsingLoadingPhases isEmbedView={isEmbedView} {...rest} />;
};
const AsyncIssueAppUsingLoadingPhases = ({ isEmbedView, ...rest }: IssueAppWithBoundaryProps) => (
	<IssueBoundary
		packageName="issue-navigator"
		isEmbedView={isEmbedView}
		render={() => <UnknownErrorView />}
	>
		<AsyncIssueAppComponent {...rest} />
	</IssueBoundary>
);
const IssueAppFG = componentWithFG(
	'nin_issue_app_conditional_entrypoint',
	IssueAppWithConditionalEntrypoint,
	AsyncIssueAppUsingLoadingPhases,
);

// oxlint-disable-next-line prefer-set-has
const issueCardChangeExcludedEventTypes: string[] = [
	ChangeEventTypes.FIELD_CHANGE_REQUESTED,
	ChangeEventTypes.FIELD_CHANGE_FAILED,
	ChangeEventTypes.CHILD_ISSUE_ADDED,
	ChangeEventTypes.ISSUE_CHILDREN_ORDER_CHANGED,
];
export type IssueAppWithDataProps = {
	/**
	 * Flag to adjust the container spacing based on whether the component is in an embedded or full page layout.
	 */
	isEmbedView: boolean;
	/**
	 * Relay fragment for issue search data.
	 */
	issueResults: IssueAppKey | null;
	view: ViewKey | null;
	/**
	 * Injected IssueApp dependency.
	 */
	IssueApp?: ComponentType<IssueAppWithBoundaryProps>;
	/**
	 * Optional react node to render in the header above the issue app.
	 */
	extraHeaderActions?: ReactNode;
	/**
	 * Optional "Back" button URL passed to the Issue App, primarily for use in
	 * Full Page Issue App mode.
	 */
	backButtonUrl?: string;
	routePathname: string;
	routeSearch: string;
	/**
	 * Optional analyticsSource to pass to issue app. Can affect document title.
	 */
	analyticsSource?: AnalyticsSource;
	issueAppEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
};

type CompatibleActionSignatures = {
	deselectIssue: () => void;
	setSelectedIssueByKey: (issueKey: IssueKey) => void;
	exitFullPageIssueAppMode: () => void;
	onDeleteIssue: (issueKeyToDelete: string) => string;
	onIssueByFieldsRefetch: (issueKey: string) => void;
};

const useJSCM1CompatibleActions = (
	connectionId: string | undefined,
	fieldSetIds: string[],
): CompatibleActionSignatures => {
	const { deselectIssue, exitFullPageIssueAppMode, setSelectedIssueByKey } =
		useSelectedIssueActions();

	const onDeleteIssue = useOnDeleteIssue(connectionId);
	const onIssueByFieldsRefetch = useIssueByFieldsRefetch(fieldSetIds);

	return {
		deselectIssue,
		exitFullPageIssueAppMode,
		setSelectedIssueByKey,
		onDeleteIssue,
		onIssueByFieldsRefetch,
	};
};

const IssueAppWithData = ({
	IssueApp = IssueAppFG,
	analyticsSource,
	extraHeaderActions,
	isEmbedView,
	issueResults,
	view,
	backButtonUrl,
	routePathname,
	routeSearch,
	issueAppEntryPoint,
}: IssueAppWithDataProps) => {
	markOnce(marks.ISSUE_APP_START);
	useLayoutEffect(() => {
		markOnce(marks.ISSUE_APP_END);
	}, []);

	const selectedIssueKey = useSelectedIssueDebouncedState();
	const [, issueContextActions] = useIssueContextActions();

	const issueResultsData = useFragment<IssueAppKey>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueAppWithData_issueResults on JiraIssueConnection {
				__id
				...headerActions_issueNavigator
			}
		`,
		issueResults,
	);

	const viewData = useFragment<ViewKey>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueAppWithData_view on JiraIssueSearchViewMetadata {
				fieldSets(
					first: $amountOfColumns
					filter: { fieldSetSelectedState: SELECTED }
					scope: $fieldSetsScope
				) {
					edges {
						node {
							fieldSetId
						}
					}
				}
			}
		`,
		view,
	);

	const fieldSetIds = useMemo(
		() => viewData?.fieldSets?.edges?.map((edge) => edge?.node?.fieldSetId).filter(Boolean) ?? [],
		[viewData?.fieldSets?.edges],
	);

	const { onInteractive } = useStartNinIssueAppAnalytics(selectedIssueKey);

	const issueAppMetrics = useMemo(
		() => ({
			onInteractive,
		}),
		[onInteractive],
	);

	const {
		deselectIssue,
		exitFullPageIssueAppMode,
		setSelectedIssueByKey,
		onDeleteIssue,
		onIssueByFieldsRefetch,
	} = useJSCM1CompatibleActions(issueResultsData?.__id, fieldSetIds);

	const refetchCallback = useRef(onIssueByFieldsRefetch);
	refetchCallback.current = onIssueByFieldsRefetch;

	const onIssueDeleteSuccess = useCallback(
		({ issueKey: deletedIssueKey }: { issueKey: string }) => {
			const issueKeyToSelect = onDeleteIssue(deletedIssueKey);
			if (!issueKeyToSelect.length) {
				deselectIssue();
				// Return to list view if we were in full page mode
				exitFullPageIssueAppMode();
			} else {
				setSelectedIssueByKey(issueKeyToSelect);
			}
		},
		[deselectIssue, exitFullPageIssueAppMode, onDeleteIssue, setSelectedIssueByKey],
	);

	const onIssueKeyChange = useCallback(
		(issueKeys: OnIssueKeyChangeArgument) => {
			setSelectedIssueByKey(issueKeys.toIssueKey);
		},
		[setSelectedIssueByKey],
	);

	// When we're in non-embedded view (i.e. full page) we need to define a max width to ensure correct full page
	// styling within the issue app.
	const maxWidth = !isEmbedView ? getIssueContainerIncreasedMaxWidth() : undefined;

	let isFullPageIssueAppMode = false;
	if (isVisualRefreshEnabled()) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isFullPageIssueAppMode = useIsFullPageIssueAppMode();
	}

	const shouldWrapMargin = !isFullPageIssueAppMode && isVisualRefreshEnabled();

	useScrollToActivityFeed(selectedIssueKey);

	let issueNavigatorData: IssueNavigatorData | undefined;
	if (fg('empanada_nin_migrate_full_page_issue_nav')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const getIssueUrl = useCallback(
			(issueKey: IssueKey) => {
				// Not using browser parsing API because it changes spaces to `+`
				const params = parse(routeSearch);
				params.selectedIssue = issueKey;

				return `${routePathname}?${stringify(params)}`;
			},
			[routeSearch, routePathname],
		);

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const issueKeys = useIssueKeys();
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const issuesCount = useIssuesCount();
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		issueNavigatorData = useMemo(
			() => ({
				issueKey: selectedIssueKey,
				issueKeys,
				knownIssueKeys: issueKeys,
				issuesCount,
				getIssueUrl,
			}),
			[selectedIssueKey, issueKeys, issuesCount, getIssueUrl],
		);
	}

	const prevIssueNavigatorData = usePrevious(issueNavigatorData);

	if (issueNavigatorData && prevIssueNavigatorData !== issueNavigatorData) {
		issueContextActions.mergeIssueContext?.({ issueNavigatorData });
	}

	if (!selectedIssueKey) {
		return <SpaStatePageErrorReady />;
	}

	return (
		<MaybeUFOSegment>
			<IssueAppContainer
				data-vc="issue-app-container"
				isEmbedView={isEmbedView || fg('empanada_nin_migrate_full_page_issue_nav')}
				shouldWrapMargin={shouldWrapMargin}
			>
				{!fg('empanada_nin_migrate_full_page_issue_nav') && (
					<HeaderActions
						issueResults={issueResultsData ?? null}
						extraHeaderActions={extraHeaderActions}
						isEmbedView={isEmbedView}
						maxWidth={maxWidth}
					/>
				)}
				<IssueAppHeightAdjustment>
					<ContextualAnalyticsData sourceName="issueNavigatorEmbeddedIssueView" sourceType={SCREEN}>
						<IssueApp
							metrics={issueAppMetrics}
							key={selectedIssueKey}
							isEmbedView={isEmbedView}
							analyticsSource={
								fg('nin_fix_document_title_in_detail_view')
									? analyticsSource || ANALYTICS_SOURCE
									: ANALYTICS_SOURCE
							}
							issueKey={selectedIssueKey}
							issueMaxWidth={maxWidth}
							onIssueKeyChange={onIssueKeyChange}
							onChange={(event: ChangeEvent) => {
								if (event && !issueCardChangeExcludedEventTypes.includes(event.type)) {
									refetchCallback.current(String(selectedIssueKey));
								}
							}}
							onIssueDeleteSuccess={onIssueDeleteSuccess}
							shouldSetInitialFocus={false}
							backButtonUrl={backButtonUrl}
							issueNavigatorData={issueNavigatorData}
							issueAppEntryPoint={issueAppEntryPoint}
						/>
					</ContextualAnalyticsData>
				</IssueAppHeightAdjustment>
			</IssueAppContainer>
		</MaybeUFOSegment>
	);
};

export default IssueAppWithData;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueAppContainer = styled.div<{
	isEmbedView: boolean; // Cleanup with empanada_nin_migrate_full_page_issue_nav
	shouldWrapMargin: boolean;
}>(
	{
		position: 'relative',
		flexGrow: 1,
		height: '100%',
		/* Setting the min width to 0 allows the div to grow/shrink to the size of the flex parent, instead of being based
    on the intrinsic content size. */
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		boxSizing: 'border-box',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ shouldWrapMargin }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		shouldWrapMargin ? css({ marginRight: token('space.negative.300') }) : undefined,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isEmbedView }) =>
		!isEmbedView
			? /* When we're not in embedded view (i.e. full page mode) we need additional padding on top of our container. */
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					paddingTop: token('space.400'),
				})
			: undefined,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueAppHeightAdjustment = styled.div({
	height: '100%',
	/* Setting the min-height to 0 allows the div to grow/shrink to the size of the flex parent, instead of being based
    on the intrinsic content height. */
	minHeight: 0,
});
